import React, { useMemo, useState } from 'react';
import Separator from '../../../../../components/Separator/Separator';
import { Col, Form, Row } from 'react-bootstrap';
import { Control, Controller } from 'react-hook-form';
import CustomRadioGroup from '../../../../../components/CustomRadioGroup/CustomRadioGroup';
import { useTranslation } from 'react-i18next';
import { OphtExaminationResult } from '../../../../../constants';
import useUserList from '../../../../../hooks/useFMDList';
import CustomSelect from '../../../../../components/CustomSelect/CustomSelect';
import CustomDateTimePicker from '../../../../../components/CustomDateTimePicker/CustomDateTimePicker';
import { RegDExamSubProps } from '../AddREGDExam';

function ReferOpht({ control, errors, setValue, data }: RegDExamSubProps) {
  const { t } = useTranslation();
  const [resultValue, setResultValue] = useState();
  const [funding, setFunding] = useState();
  const [nextAppointmentValue, setNextAppointmentValue] = useState();

  const { list: ophtList } = useUserList('opht');

  const values = useMemo(
    () => [
      {
        label: t('examinations:regdExam.general.resultValue1'),
        checked: resultValue === 1,
        value: 1,
      },
      {
        label: t('examinations:regdExam.general.resultValue2'),
        checked: resultValue === 2,
        value: 2,
      },
      // {
      //   label: t('examinations:fmdExam.general.resultValue3'),
      //   checked: resultValue === 3,
      //   value: 3,
      // },
    ],
    [resultValue]
  );

  const fundingValues = useMemo(
    () => [
      {
        label: t('examinations:fmdExam.general.fundingValue1'),
        checked: funding === 'aecp',
        value: 'aecp',
      },
      {
        label: t('examinations:fmdExam.general.fundingValue2'),
        checked: funding === 'state',
        value: 'state',
      },
    ],
    [funding]
  );

  const nextAppointmentValues = useMemo(
    () => [
      {
        label: t('yes'),
        checked: nextAppointmentValue === true,
        value: true,
      },
      {
        label: t('no'),
        checked: nextAppointmentValue === false,
        value: false,
      },
    ],
    [nextAppointmentValue]
  );

  return (
    <>
      <Separator text={t('examinations:regdExam.general.referral')} />
      <Row>
        <Col>
          <CustomRadioGroup
            label={t('examinations:regdExam.general.result')}
            labelClass="d-block"
            items={values}
            inline={false}
            groupProps={{ className: 'mb-3' }}
            controlProps={{
              name: 'result',
              control,
              rules: { validate: v => v !== undefined || 'required' },
              defaultValue: data?.result,
            }}
            onChange={v => {
              setResultValue(v);
              setValue('result', v);
            }}
          />
        </Col>
      </Row>
      {resultValue === OphtExaminationResult.referedToOphthalmologist ? (
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t('userRoles.opht')}</Form.Label>
              {ophtList.length > 0 && (
                <Controller
                  name="ophthalmologist"
                  control={control}
                  render={({ field: { onChange, value, onBlur } }) => {
                    return (
                      <CustomSelect
                        style={{ textAlign: 'left' }}
                        options={ophtList}
                        value={value}
                        onBlur={onBlur}
                        contentRenderer={({ state }) => {
                          const { values } = state;

                          return (
                            <span className="text-size-smaller">
                              {values[0] &&
                                values[0].value &&
                                values[0].label + (values[0].info?.company ? ` (${values[0].info.company})` : '')}
                            </span>
                          );
                        }}
                        itemRenderer={({ item, methods }) => (
                          <span
                            role="option"
                            aria-selected="false"
                            className="react-dropdown-select-item"
                            onClick={() => {
                              methods.addItem(item);
                            }}
                          >
                            {item && item.value && item.label + (item.info?.company ? ` (${item.info.company})` : '')}
                          </span>
                        )}
                        onChange={(val: Array<any>) => {
                          onChange(val[0].value);
                        }}
                      />
                    );
                  }}
                />
              )}
            </Form.Group>
          </Col>
          {/* <Col>Institution</Col> */}
          <Col>
            <Form.Group className="mb-2">
              <Form.Label>{t('examinations:fmdExam.general.dateRange')}</Form.Label>
              <CustomDateTimePicker
                controlProps={{ control, name: 'dateRange' }}
                error={errors?.dateRange}
                minDate={new Date()}
                range={true}
                format="yyyy-MM-dd"
                onChange={v => {
                  setValue('dateRange', v);
                }}
              />
              {errors?.date && (
                <Form.Control.Feedback type="invalid">
                  {(errors?.date.message && t(`errors:validationMessages.${errors?.date.message}`)) ||
                    (errors?.date.type === 'required' && (
                      <span id={'error-date'}>{t('errors:validationMessages.required')}</span>
                    ))}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col>
          <CustomRadioGroup
            label={t('examinations:regdExam.general.nextAppointment')}
            labelClass="d-block"
            items={nextAppointmentValues}
            groupProps={{ className: 'mb-3' }}
            controlProps={{
              name: 'nextAppointmentValue',
              control,
              rules: { validate: v => v !== undefined || 'required' },
              defaultValue: data?.nextAppointmentValue,
            }}
            onChange={v => {
              setNextAppointmentValue(v);
              setValue('nextAppointmentValue', v);
            }}
          />
        </Col>
        <Col>
          <Form.Group className="mb-2">
            <Form.Label>{t('examinations:fmdExam.general.nextAppointmentDate')}</Form.Label>
            <CustomDateTimePicker
              controlProps={{ control, name: 'nextAppointmentDate' }}
              error={errors?.nextAppointmentDate}
              minDate={new Date()}
              range={true}
              format="yyyy-MM-dd"
              onChange={v => {
                setValue('nextAppointmentDate', v);
              }}
            />
            {errors?.date && (
              <Form.Control.Feedback type="invalid">
                {(errors?.date.message && t(`errors:validationMessages.${errors?.date.message}`)) ||
                  (errors?.date.type === 'required' && (
                    <span id={'error-date'}>{t('errors:validationMessages.required')}</span>
                  ))}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>
    </>
  );
}

export default ReferOpht;
